import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, Button } from 'react-bootstrap';
import Markdown from 'react-markdown';
import {
  IconPlus,
  IconGripVertical,
  IconInfoCircle,
  IconRocket,
} from '@tabler/icons-react';
import { NODE_CATEGORIES } from '../constants/nodeCategories';
import ProBadge from '../../components/ProBadge';

// =============================================================================
// SIDEBAR COMPONENT
// =============================================================================
const Sidebar = ({ onDragStart, isCollapsed, onToggle, nodes, isProPlan, onProClick }) => {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('actions'); // Tab inicial: Ações
  const [infoCategoryId, setInfoCategoryId] = useState(null);
  const [showProInfoModal, setShowProInfoModal] = useState(false);
  const activeItems = NODE_CATEGORIES.find(c => c.id === activeCategory)?.items || [];
  const infoCategory = NODE_CATEGORIES.find(c => c.id === infoCategoryId);

  // Contar instâncias de cada key nos nodes do canvas
  const instanceCounts = React.useMemo(() => {
    const counts = {};
    (nodes || []).forEach(node => {
      const key = node.data?.key;
      if (key) counts[key] = (counts[key] || 0) + 1;
    });
    return counts;
  }, [nodes]);

  return (
    <>
      <div className={`afc-sidebar ${isCollapsed ? 'collapsed' : ''}`}>
        <div className="afc-sidebar-header">
          <div className="afc-sidebar-title">
            <IconPlus size={18} />
            <span>Componentes</span>
          </div>
          <button className="afc-sidebar-toggle" onClick={onToggle}>
            <IconGripVertical size={16} />
          </button>
        </div>

        {!isCollapsed && (
          <>
            <div className="afc-category-tabs">
              {NODE_CATEGORIES.map(cat => (
                <button
                  key={cat.id}
                  className={`afc-category-tab ${activeCategory === cat.id ? 'active' : ''}`}
                  onClick={() => setActiveCategory(cat.id)}
                >
                  {cat.label}
                </button>
              ))}
            </div>

            <div className="afc-sidebar-content">
              <p className="afc-sidebar-hint">
                {NODE_CATEGORIES.find(c => c.id === activeCategory)?.description}
                <button
                  className="afc-info-btn"
                  onClick={() => setInfoCategoryId(activeCategory)}
                  title="O que é isso?"
                >
                  <IconInfoCircle size={15} />
                </button>
              </p>

              <div className="afc-node-list">
                {activeItems.map(item => {
                  const Icon = item.icon;
                  const isAtLimit = item.maxInstances && (instanceCounts[item.key] || 0) >= item.maxInstances;
                  const isDisabled = isAtLimit;
                  return (
                    <div
                      key={item.key}
                      className={`afc-node-item ${isDisabled ? 'disabled' : ''}`}
                      draggable={!isDisabled}
                      onDragStart={(e) => !isDisabled && onDragStart(e, item)}
                      title={isAtLimit ? item.maxInstancesMessage : ''}
                    >
                      <div className="afc-node-item-icon" style={{ background: item.gradient, opacity: isDisabled ? 0.4 : 1 }}>
                        <Icon size={18} stroke={1.5} />
                      </div>
                      <div className="afc-node-item-info">
                        <span className="afc-node-item-label" style={{ opacity: isDisabled ? 0.5 : 1 }}>
                          {item.label} {isAtLimit ? '(em uso)' : ''} {item.pro && !isProPlan && <ProBadge small />}
                        </span>
                        <span className="afc-node-item-desc" style={{ opacity: isDisabled ? 0.5 : 1 }}>
                          {isAtLimit ? 'Limite: 1 por fluxo' : item.description}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Entenda o PRO - bottom da sidebar */}
            {!isProPlan && (
              <div className="afc-sidebar-pro-cta">
                <button className="afc-sidebar-pro-btn" onClick={() => setShowProInfoModal(true)}>
                  <span className="pro-badge" style={{ cursor: 'pointer' }}>
                    <span className="pro-badge__icon"><IconInfoCircle size={18} stroke={2.5} /></span>
                    Entenda o PRO
                  </span>
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal explicativo da categoria */}
      <Modal
        show={!!infoCategoryId}
        onHide={() => setInfoCategoryId(null)}
        centered
        size="lg"
        className="afc-info-modal"
      >
        <Modal.Header closeButton className="afc-info-modal__header">
          <Modal.Title className="afc-info-modal__title">
            <IconInfoCircle size={22} className="afc-info-modal__title-icon" />
            {infoCategory?.infoTitle}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="afc-info-modal__body">
          <Markdown>{infoCategory?.infoContent || ''}</Markdown>
        </Modal.Body>
        <Modal.Footer className="afc-info-modal__footer">
          <Button variant="primary" size="sm" onClick={() => setInfoCategoryId(null)}>
            Entendi!
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal Entenda o PRO */}
      <Modal
        show={showProInfoModal}
        onHide={() => setShowProInfoModal(false)}
        centered
        className="afc-info-modal"
      >
        <Modal.Header closeButton className="afc-pro-modal__header">
          <Modal.Title className="afc-pro-modal__title">
            <span className="pro-badge" style={{ fontSize: 12, padding: '2px 8px', lineHeight: '18px' }}>
              PRO
            </span>
            O que são recursos PRO?
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="afc-pro-modal__body">
          <p className="afc-pro-modal__intro">
            Recursos marcados com a tag <span className="pro-badge" style={{ fontSize: 9, padding: '0px 4px', lineHeight: '14px', verticalAlign: 'middle' }}>PRO</span> são
            funcionalidades premium do plano <strong>Business</strong>.
          </p>

          <div className="afc-pro-modal__section">
            <h4>O que posso fazer agora?</h4>
            <p>
              Você pode <strong>usar e testar todos os recursos PRO livremente</strong> durante
              a construção do seu agente. Explore, configure e veja como funcionam sem
              nenhuma restrição no playground de testes.
            </p>
          </div>

          <div className="afc-pro-modal__section">
            <h4>Quando preciso do upgrade?</h4>
            <p>
              Para <strong>publicar</strong> um agente que utiliza recursos PRO, é necessário
              estar no plano Business. Ao tentar publicar, você será informado sobre quais
              recursos PRO estão no seu fluxo.
            </p>
          </div>

          <div className="afc-pro-modal__section">
            <h4>Quais são os recursos PRO?</h4>
            <ul className="afc-pro-modal__list">
              <li><strong>Chamada de API</strong> — Integração com sistemas externos</li>
              <li><strong>Envio de Email</strong> — Emails automáticos com HTML gerado por IA</li>
              <li><strong>Variáveis</strong> — Armazenar e reutilizar dados no fluxo</li>
              <li><strong>Áudios na Galeria</strong> — Gravar e enviar áudios pelo agente</li>
              <li><strong>Websites na Base de Conhecimento</strong> — Webscraping automático</li>
            </ul>
          </div>
        </Modal.Body>
        <Modal.Footer className="afc-pro-modal__footer">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => setShowProInfoModal(false)}
          >
            Fechar
          </Button>
          <Button
            size="sm"
            className="afc-pro-modal__upgrade-btn"
            onClick={() => {
              setShowProInfoModal(false);
              navigate('/my-plan');
            }}
          >
            <IconRocket size={16} />
            Ver Planos
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default Sidebar;
